import {ensureConnection} from "../websocket/websocketUtils.ts";

declare global {
    interface Window {
        audioElement?: HTMLAudioElement;
    }
}

export const stopPlaying = (setDynamicStateObj: any, miscMidRenderRefs: any) => {
    miscMidRenderRefs.current.playingId = null;
    setDynamicStateObj({playingId: null});

    if (window.audioElement) {
        window.audioElement.pause();
        window.audioElement.currentTime = 0;
    }
};

const playAudioBlob = (blob: Blob, audioElement: HTMLAudioElement): Promise<void> => {
    const audioUrl = URL.createObjectURL(blob);
    audioElement.src = audioUrl;
    return new Promise((resolve) => {
        audioElement.onended = () => {
            URL.revokeObjectURL(audioUrl);
            resolve();
        };
        audioElement.play();
    });
};

export const handleAudioClick = async (
    websocketRef: any,
    id: string,
    text: string,
    setDynamicStateObj: any,
    miscMidRenderRefs: any,
    voiceId: string,
    setupWebsocketHelper: any
) => {
    await ensureConnection(websocketRef, setupWebsocketHelper);

    if (miscMidRenderRefs.current.playingId && miscMidRenderRefs.current.playingId === id) {
        stopPlaying(setDynamicStateObj, miscMidRenderRefs);
        return;
    }

    if (miscMidRenderRefs.current.playingId !== null) {
        stopPlaying(setDynamicStateObj, miscMidRenderRefs);
    }

    setDynamicStateObj({playingId: id});
    miscMidRenderRefs.current.playingId = id;

    // Create audio element if it doesn't exist
    if (!window.audioElement) {
        window.audioElement = new Audio();
    }
    const audioElement = window.audioElement;

    try {
        if (!websocketRef.current || websocketRef.current.readyState !== WebSocket.OPEN) {
            console.error('WebSocket is not connected');
        }

        // Create a queue to store ready-to-play blobs
        const audioQueue: Blob[] = [];
        let isPlaying = false;
        let isDone = false;

        // Function to play the next blob in the queue
        const playNext = async () => {
            if (audioQueue.length === 0) {
                if (isDone) {
                    stopPlaying(setDynamicStateObj, miscMidRenderRefs);
                }
                return;
            }
            if (isPlaying || !miscMidRenderRefs.current.playingId) return;

            isPlaying = true;
            const nextBlob = audioQueue.shift();
            if (nextBlob) {
                try {
                    await playAudioBlob(nextBlob, audioElement);
                } catch (error) {
                    console.error('Error playing audio blob:', error);
                }
                isPlaying = false;
                playNext(); // Try to play the next one if available
            }
        };

        // Add event listener for when each chunk finishes playing
        audioElement.onended = () => {
            isPlaying = false;
            playNext();
        };

        // Set up message handler for TTS responses
        const handleTTSResponse = async (event: MessageEvent) => {
            if (!miscMidRenderRefs.current.playingId) {
                websocketRef.current.removeEventListener('message', handleTTSResponse);
                return;
            }

            const { functionName, args } = JSON.parse(event.data);

            if (functionName === 'ttsResponse') {
                if (args.type === 'audioChunk') {
                    const binaryString = atob(args.audioContent);
                    const bytes = new Uint8Array(binaryString.length);
                    for (let i = 0; i < binaryString.length; i++) {
                        bytes[i] = binaryString.charCodeAt(i);
                    }
                    const audioBlob = new Blob([bytes], { type: 'audio/wav' });

                    audioQueue.push(audioBlob);
                    if (!isPlaying) {
                        playNext();
                    }
                } else if (args.type === 'done') {
                    websocketRef.current.removeEventListener('message', handleTTSResponse);
                    isDone = true;
                    if (!isPlaying && audioQueue.length === 0) {
                        stopPlaying(setDynamicStateObj, miscMidRenderRefs);
                    }
                } else if (args.type === 'error') {
                    console.error('TTS error:', args.error);
                    stopPlaying(setDynamicStateObj, miscMidRenderRefs);
                }
            }
        };

        websocketRef.current.addEventListener('message', handleTTSResponse);

        // Send TTS request
        websocketRef.current.send(JSON.stringify({
            functionName: 'textToSpeech',
            args: {
                text,
                voiceId
            }
        }));

    } catch (error) {
        console.error(`Error in TTS: ${error}`);
        stopPlaying(setDynamicStateObj, miscMidRenderRefs);
    }
};